import React, { useCallback, useEffect, useState } from "react";
import { RETURN_METHOD_RETURN } from "~/constants";
import { getRecommendedExchangeProducts, getRefundOptions, getReturnReasonTranslated, getSelectedRefundOption } from "~/cores/appCore";
import { Button } from "~/components/buttons/Buttons";
import OneClickOptionsWrapper, { OneClickTextOption } from "../OneClickOptions";
import { TurnrModalContainer, TurnrModalContent, TurnrModalHeader } from "~/components/modals/TurnrModal";
import ProductItem from "~/components/productItem/ProductItem";
import TextArea from "~/components/inputs/TextArea";
import { createExchangeMethodState } from "../exchange/ExchangeStepper";
import PaymentMethodStep from "../PaymentMethodStep";
import { isOnlyProductReturnEnabled, isProductExchangeDisabled } from "~/cores/productCore";
import { RETURN_REASON_TYPES } from "rylak-common/src/constants";

export function createReturnMethodState({ product, returnReasons = [] }) {
    return {
        productId: product?.sessionItemId,
        returnReasons: returnReasons,
        returnMethodType: RETURN_METHOD_RETURN,
    };
}

// TODO Duplicate from "backoffice/src/cores/returnReasonCore";
export function isReturnReasonSimpleCheckbox(returnReason) {
    return (returnReason?.type === RETURN_REASON_TYPES.SIMPLE || returnReason?.type === RETURN_REASON_TYPES.CHECKBOX) && !(returnReason?.subReasons?.length > 0);
}

function RecommendedExchangeStep({ appProps, onModalClose, product, selectedReturnMethodData, recommendedExchangeProducts, setRecommendedExchangeProducts, attemptOnSave }) {
    const { getLocaleText } = appProps;
    return (
        <>
            <TurnrModalHeader onBack={() => setRecommendedExchangeProducts(null)} onClose={onModalClose} title={getLocaleText("recommended-exchange-title")} />
            <TurnrModalContent>
                <OneClickOptionsWrapper>
                    <OneClickTextOption dataTestId={"no-thanks"} onClick={() => attemptOnSave(selectedReturnMethodData)}>
                        {getLocaleText("no-thanks")}
                    </OneClickTextOption>
                    {recommendedExchangeProducts?.map((p, index) => {
                        return (
                            <ProductItem
                                key={index}
                                getLocaleText={getLocaleText}
                                tight={true}
                                smallImage={true}
                                product={{ ...product, ...p }}
                                onClick={() =>
                                    attemptOnSave(
                                        createExchangeMethodState({
                                            fromProduct: product,
                                            variant: p,
                                        }),
                                    )
                                }
                            />
                        );
                    })}
                </OneClickOptionsWrapper>
            </TurnrModalContent>
        </>
    );
}

function TextBoxStep({ appProps, product, selectedReason, selectedSubReason, selectedReturnMethodData, onModalClose, attemptOnSave, onTextBoxStepBack }) {
    const { language, addAlert, getLocaleText } = appProps;
    const [textValue, setTextValue] = useState(selectedReturnMethodData?.returnReasons?.[0]?.text || selectedReturnMethodData?.returnReasons?.[0]?.subReasons?.[0]?.text || "");
    const [validated, setValidated] = useState(false);

    const returnReasonLabel = getReturnReasonTranslated({
        returnReason: selectedSubReason || selectedReason,
        language,
    });
    const MIN_LETTERS = 5;
    const textBoxIsValid = textValue?.length > MIN_LETTERS;

    const onSaveClick = useCallback(() => {
        if (!textBoxIsValid) {
            setValidated(true);
            addAlert({
                content: `Please write more than ${MIN_LETTERS} letters`,
            });
        } else {
            const returnMethodData = createReturnMethodState({
                product,
                returnReasons: [
                    {
                        id: selectedReason.id,
                        text: selectedSubReason ? null : textValue,
                        subReasons: selectedSubReason ? [{ id: selectedSubReason.id, text: textValue }] : null,
                    },
                ],
            });

            attemptOnSave(returnMethodData);
        }
    }, [textValue, textBoxIsValid, addAlert]);
    return (
        <>
            <TurnrModalHeader onBack={onTextBoxStepBack} onClose={onModalClose} title={returnReasonLabel} />
            <TurnrModalContent>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "30px",
                    }}
                >
                    <TextArea
                        value={textValue}
                        onChange={(e) => {
                            setTextValue(e.target.value);
                        }}
                        error={validated && !textBoxIsValid}
                        floatingText={"The reason I'm returning is..."}
                    />
                    <Button
                        appProps={appProps}
                        fullWidth
                        text={getLocaleText("continue")}
                        onClick={() => {
                            onSaveClick();
                        }}
                    />
                </div>
            </TurnrModalContent>
        </>
    );
}

function SubReturnReasonStep({ appProps, product, selectedReason, setSelectedReason, setSelectedSubReason, onModalClose, attemptOnSave }) {
    const { language } = appProps;
    const selectedReasonTranslated = getReturnReasonTranslated({
        returnReason: selectedReason,
        language,
    });

    const onClick = useCallback((subReturnReason) => {
        if (subReturnReason?.type !== RETURN_REASON_TYPES.FREE_TEXT) {
            const returnMethodData = createReturnMethodState({
                product,
                returnReasons: [
                    {
                        id: selectedReason.id,
                        subReasons: [{ id: subReturnReason.id }],
                    },
                ],
            });
            attemptOnSave(returnMethodData);
        } else {
            setSelectedSubReason(subReturnReason);
        }
    }, []);

    return (
        <>
            <TurnrModalHeader onBack={() => setSelectedReason(null)} onClose={onModalClose} title={selectedReasonTranslated} />
            <TurnrModalContent>
                <OneClickOptionsWrapper>
                    {selectedReason?.subReasons?.map((subReturnReason) => {
                        return (
                            <OneClickTextOption dataTestId={`return-sub-reason-${subReturnReason?.id}`} key={subReturnReason?.id} onClick={() => onClick(subReturnReason)}>
                                {getReturnReasonTranslated({
                                    returnReason: subReturnReason,
                                    language,
                                })}
                            </OneClickTextOption>
                        );
                    })}
                </OneClickOptionsWrapper>
            </TurnrModalContent>
        </>
    );
}

function ReturnReasonStep({ appProps, product, setSelectedReason, onModalClose, attemptOnSave, backToMethodSelector }) {
    const { language, getLocaleText } = appProps;

    const onClick = useCallback((returnReason) => {
        //TODO Very ugly. refactor this
        if (!returnReason?.isTextRequired && isReturnReasonSimpleCheckbox(returnReason)) {
            const returnMethodData = createReturnMethodState({ product, returnReasons: [{ id: returnReason.id }] });
            attemptOnSave(returnMethodData);
        } else {
            setSelectedReason(returnReason);
        }
    }, []);

    const onlyReturnEnabled = isOnlyProductReturnEnabled(product, { appProps });

    return (
        <>
            <TurnrModalHeader onBack={!onlyReturnEnabled && backToMethodSelector} onClose={onModalClose} title={getLocaleText("why-return-it")} />
            <TurnrModalContent>
                <OneClickOptionsWrapper>
                    {product?.returnReasonConfig?.returnReasons?.map((returnReason) => {
                        return (
                            <OneClickTextOption dataTestId={`return-reason-${returnReason?.id}`} key={returnReason?.id} onClick={() => onClick(returnReason)}>
                                {getReturnReasonTranslated({ returnReason, language })}
                            </OneClickTextOption>
                        );
                    })}
                </OneClickOptionsWrapper>
            </TurnrModalContent>
        </>
    );
}

function ReturnStepper(props) {
    const { product, onSave, onModalClose, backToMethodSelector, appProps, productExchangeServiceData } = props;

    const [selectedReason, setSelectedReason] = useState(null);
    const [selectedSubReason, setSelectedSubReason] = useState(null);

    // This is only used for remembering the "old choice" for recommended exchange products
    const [selectedReturnMethodData, setSelectedReturnMethodData] = useState(null);
    const [recommendedExchangeProducts, setRecommendedExchangeProducts] = useState(null);
    const [showPaymentMethodStep, setShowPaymentMethodStep] = useState(false);
    const hasMultipleRefundOptions = getRefundOptions(appProps)?.length > 1;

    useEffect(() => {
        setSelectedReturnMethodData(null);
    }, [selectedReason]);

    const attemptOnSave = useCallback(
        (returnMethodData) => {
            setSelectedReturnMethodData(returnMethodData);
            const exchangeProducts = getRecommendedExchangeProducts({
                returnMethodData: returnMethodData,
                product,
                productExchangeServiceData,
            });
            if (!isProductExchangeDisabled(product, { appProps }) && exchangeProducts?.length > 0 && !recommendedExchangeProducts) {
                setRecommendedExchangeProducts(exchangeProducts);
            } else if (hasMultipleRefundOptions && !getSelectedRefundOption(appProps)) {
                setShowPaymentMethodStep(true);
            } else {
                onSave(returnMethodData);
            }
        },
        [recommendedExchangeProducts, product, productExchangeServiceData, appProps, hasMultipleRefundOptions],
    );

    function onPaymentMethodSave(refundOptionType) {
        onSave(selectedReturnMethodData, refundOptionType);
    }

    const onTextBoxStepBack = useCallback(() => {
        if (selectedSubReason) {
            setSelectedSubReason(null);
        } else {
            setSelectedReason(null);
        }
    }, [selectedReason, selectedSubReason]);

    const stepProps = {
        appProps,
        product,
        backToMethodSelector,
        onModalClose,
        selectedReason,
        setSelectedReason,
        selectedSubReason,
        selectedReturnMethodData,
        setSelectedReturnMethodData,
        recommendedExchangeProducts,
        setRecommendedExchangeProducts,
        setShowPaymentMethodStep,
        attemptOnSave,
        setSelectedSubReason,
        onTextBoxStepBack,
        onPaymentMethodSave,
    };

    const showRecommendedExchangeStep = !showPaymentMethodStep && recommendedExchangeProducts?.length > 0;
    const showReturnReasonStep = !showPaymentMethodStep && !showRecommendedExchangeStep && !selectedReason;
    const showSubReturnReasonStep =
        !showPaymentMethodStep && !showRecommendedExchangeStep && (selectedReason?.type === RETURN_REASON_TYPES.MULTIPLE_CHOICE || selectedReason?.subReasons?.length > 0) && !selectedSubReason;
    const showTextBoxAnswerStep =
        !showPaymentMethodStep &&
        !showRecommendedExchangeStep &&
        (selectedReason?.type === RETURN_REASON_TYPES.FREE_TEXT || selectedSubReason?.type === RETURN_REASON_TYPES.FREE_TEXT || selectedReason?.isTextRequired);

    return (
        <TurnrModalContainer>
            {showReturnReasonStep && <ReturnReasonStep {...stepProps} />}
            {showSubReturnReasonStep && <SubReturnReasonStep {...stepProps} />}
            {showTextBoxAnswerStep && <TextBoxStep {...stepProps} />}
            {showRecommendedExchangeStep && <RecommendedExchangeStep {...stepProps} />}
            {showPaymentMethodStep && <PaymentMethodStep {...stepProps} onBack={() => setShowPaymentMethodStep(false)} />}
        </TurnrModalContainer>
    );
}

export default ReturnStepper;
